// web/src/console/sections/UsageSection.tsx
import type { JSX } from "react";
import { adminApi } from "../../lib/adminApi";
import { useConsole } from "../ConsoleContext";
import { useAsync } from "../useAsync";
import { LineChart, StackedBar, StatCard } from "../charts";
import { CardTitle, Term } from "../Term";
import { fmtCount, fmtMs, fmtTokens, fmtCny } from "../format";

const summaryStyle = {
  fontSize: "var(--text-caption)",
  color: "var(--text-tertiary)",
  margin: "2px 0 var(--space-2)",
  fontVariantNumeric: "tabular-nums"
} as const;

// Categorical colours for the prompt/completion stacked bar (with a legend).
const TOKEN_IN_COLOR = "var(--cc-cat-1)";
const TOKEN_OUT_COLOR = "var(--cc-cat-3)";
const TOKEN_LEGEND = [
  { label: "输入 token", color: TOKEN_IN_COLOR },
  { label: "输出 token", color: TOKEN_OUT_COLOR }
];

/**
 * Usage section — /admin/usage (#228 Phase 3).
 *
 * Panels: DAU + daily active chats, avg turns/chat, avg token/turn, token volume,
 * prompt vs completion (StackedBar), estimated cost, TTFT/p95 latency, cache
 * savings (缓存读取 token + estimatedSavedTokens labeled 估算), top-users table.
 * Fetches via adminApi.usage() keyed on range + from/to + live tick.
 */
export function UsageSection(): JSX.Element {
  const { query, tick } = useConsole();
  const { data, loading, error, reload } = useAsync(
    () => adminApi.usage({
      range: query.range,
      ...(query.from !== undefined && query.to !== undefined
        ? { from: query.from, to: query.to }
        : {})
    }),
    [query.range, query.from, query.to, tick]
  );

  if (loading) return <div className="cc-skeleton" style={{ height: 320 }} />;
  if (error || !data) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={reload}>重试</button></div>;

  const { buckets, activeUsers, activeChats, cacheSavings, topUsers, models, cacheLayers, cacheServeTrend, window: win } = data;

  // KB cache layers (#cache-layers): current state per tier + serve trend.
  const l2 = cacheLayers.find((c) => c.tier === "l2") ?? { tier: "l2" as const, entries: 0, activeEntries: 0, hits: 0 };
  const l3 = cacheLayers.find((c) => c.tier === "l3") ?? { tier: "l3" as const, entries: 0, activeEntries: 0, hits: 0 };
  const cacheServeBuckets = cacheServeTrend.map((b) => ({
    ts: b.ts,
    segments: [
      { value: b.l2, color: "var(--cc-cat-4)" },
      { value: b.l3, color: "var(--cc-cat-6)" }
    ]
  }));
  const cacheServeLegend = [
    { label: "二级知识库 (联网缓存)", color: "var(--cc-cat-4)" },
    { label: "三级知识库 (对话沉淀)", color: "var(--cc-cat-6)" }
  ];

  // Derived point series from the merged, 0-filled bucket array.
  const dauPts = buckets.map((b) => ({ ts: b.ts, value: b.dau }));
  const chatPts = buckets.map((b) => ({ ts: b.ts, value: b.activeChats }));
  const avgTurnsPts = buckets.map((b) => ({
    ts: b.ts,
    value: b.activeChats > 0 ? Math.round(b.turns / b.activeChats) : 0
  }));
  const avgTokenPts = buckets.map((b) => ({ ts: b.ts, value: b.avgTotalTokens }));
  const tokenPts = buckets.map((b) => ({ ts: b.ts, value: b.sumTotalTokens }));
  const costPts = buckets.map((b) => ({ ts: b.ts, value: b.sumCostCny }));
  const ttftP50Pts = buckets.map((b) => ({ ts: b.ts, value: b.p50Ttft }));
  const ttftP95Pts = buckets.map((b) => ({ ts: b.ts, value: b.p95Ttft }));

  // Stacked bar: prompt (输入) vs completion (输出), each its own categorical colour.
  const tokenStackedBuckets = buckets.map((b) => ({
    ts: b.ts,
    segments: [
      { value: b.sumPromptTokens, color: TOKEN_IN_COLOR },
      { value: b.sumCompletionTokens, color: TOKEN_OUT_COLOR }
    ]
  }));

  // Window-level aggregates. DAU + active chats are distinct-over-the-whole-window
  // (from the backend) — NOT peak/sum of the per-bucket series, which would
  // undercount users spread across buckets and overcount multi-bucket sessions.
  const totalTurns = buckets.reduce((s, b) => s + b.turns, 0);
  const totalCost = buckets.reduce((s, b) => s + b.sumCostCny, 0);
  const lastBucket = buckets.at(-1);
  const lastP50Ttft = lastBucket?.p50Ttft ?? 0;
  const lastP95Ttft = lastBucket?.p95Ttft ?? 0;
  const lastP95Latency = lastBucket?.p95Latency ?? 0;

  return (
    <div className="cc-grid cc-grid-2">

      {/* DAU — distinct active users over the window (headline) + per-bucket trend. */}
      <div className="cc-card">
        <CardTitle>日活用户（DAU）</CardTitle>
        <div style={summaryStyle}>窗口内 {fmtCount(activeUsers)} 人</div>
        <LineChart points={dauPts} from={win.from} to={win.to} formatY={fmtCount} />
      </div>

      {/* 日活会话 — distinct sessions over the window (headline) + per-bucket trend. */}
      <div className="cc-card">
        <CardTitle>日活会话</CardTitle>
        <div style={summaryStyle}>窗口内 {fmtCount(activeChats)} 个</div>
        <LineChart points={chatPts} from={win.from} to={win.to} stroke="var(--color-warning)" formatY={fmtCount} />
      </div>

      {/* 平均轮次/会话 — turns / distinct chats per bucket. */}
      <div className="cc-card">
        <CardTitle>平均轮次/会话</CardTitle>
        <div style={summaryStyle}>
          窗口合计 {fmtCount(totalTurns)} 轮 / {fmtCount(activeChats)} 会话
        </div>
        <LineChart points={avgTurnsPts} from={win.from} to={win.to} formatY={fmtCount} />
      </div>

      {/* 平均 token/轮 — avgTotalTokens (prompt + completion) per bucket. */}
      <div className="cc-card">
        <CardTitle>平均 token/轮</CardTitle>
        <div style={summaryStyle}>prompt + completion token 合计均值</div>
        <LineChart points={avgTokenPts} from={win.from} to={win.to} stroke="var(--color-success)" formatY={fmtTokens} />
      </div>

      {/* Token 用量趋势 — sumTotalTokens per bucket. */}
      <div className="cc-card">
        <CardTitle>token 用量趋势</CardTitle>
        <div style={summaryStyle}>每时间段的 token 用量合计</div>
        <LineChart points={tokenPts} from={win.from} to={win.to} formatY={fmtTokens} />
      </div>

      {/* 输入 vs 输出 token — prompt vs completion, distinct colours + legend. */}
      <div className="cc-card">
        <CardTitle>输入 vs 输出 token</CardTitle>
        <div style={summaryStyle}><Term>输入 token</Term> prompt · <Term>输出 token</Term> completion</div>
        <StackedBar buckets={tokenStackedBuckets} legend={TOKEN_LEGEND} from={win.from} to={win.to} formatY={fmtTokens} />
      </div>

      {/* 估算成本 — sumCostCny per bucket; must carry 估算 label. */}
      <div className="cc-card">
        <CardTitle>估算成本（¥）</CardTitle>
        <div style={summaryStyle}>
          窗口合计 {fmtCny(totalCost)}（<Term>估算成本</Term>）
        </div>
        <LineChart points={costPts} from={win.from} to={win.to} stroke="var(--color-warning)" formatY={fmtCny} />
      </div>

      {/* TTFT + p95 延迟 — first-token latency + end-to-end. */}
      <div className="cc-card">
        <CardTitle>TTFT 与 p95 延迟</CardTitle>
        <div className="cc-grid-stats">
          <StatCard label="p50 TTFT" value={fmtMs(lastP50Ttft)} />
          <StatCard label="p95 TTFT" value={fmtMs(lastP95Ttft)} />
          <StatCard
            label="p95 延迟"
            value={fmtMs(lastP95Latency)}
            {...(lastP95Latency > 3000 ? { tone: "warn" as const } : {})}
          />
        </div>
        <div style={{ marginTop: "var(--space-2)" }}>
          <LineChart points={ttftP50Pts} from={win.from} to={win.to} formatY={fmtMs} />
        </div>
        <div style={{ marginTop: "var(--space-2)" }}>
          <LineChart points={ttftP95Pts} from={win.from} to={win.to} stroke="var(--color-warning)" formatY={fmtMs} />
        </div>
      </div>

      {/* 缓存节省 — prompt-cache read tokens + answer-cache hit estimate. */}
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
        <CardTitle>缓存节省</CardTitle>
        <div className="cc-grid-stats">
          <StatCard label="缓存读取 token" value={fmtTokens(cacheSavings.cacheReadTokens)} />
          <StatCard
            label="缓存命中次数"
            value={fmtCount(cacheSavings.cacheHits)}
            {...(cacheSavings.cacheHits > 0 ? { tone: "ok" as const } : {})}
          />
          <StatCard label="估算节省 token" value={fmtTokens(cacheSavings.estimatedSavedTokens)} />
        </div>
        <div style={{ ...summaryStyle, marginTop: "var(--space-2)" }}>
          <Term>缓存节省·估算</Term>：节省量 = 缓存命中次数 × 同期非缓存轮次均值（<Term>估算</Term>，非精确核算）
        </div>
      </div>

      {/* 知识缓存（分层）— learned-knowledge cache state + serves over time. */}
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
        <CardTitle>知识缓存（分层）</CardTitle>
        <div style={summaryStyle}>
          “越用越省 token”：<Term>二级知识库</Term>（联网结果缓存）与 <Term>三级知识库</Term>（对话/转人工沉淀）当前规模与累计命中
        </div>
        <div className="cc-grid-stats">
          <StatCard label="二级·条目" value={fmtCount(l2.activeEntries)} {...(l2.activeEntries > 0 ? { tone: "ok" as const } : {})} />
          <StatCard label="二级·累计命中" value={fmtCount(l2.hits)} {...(l2.hits > 0 ? { tone: "ok" as const } : {})} />
          <StatCard label="三级·条目" value={fmtCount(l3.activeEntries)} {...(l3.activeEntries > 0 ? { tone: "ok" as const } : {})} />
          <StatCard label="三级·累计命中" value={fmtCount(l3.hits)} {...(l3.hits > 0 ? { tone: "ok" as const } : {})} />
        </div>
        <div style={{ ...summaryStyle, marginTop: "var(--space-3)" }}>缓存命中趋势（按层）</div>
        {cacheServeBuckets.length > 0
          ? <StackedBar buckets={cacheServeBuckets} legend={cacheServeLegend} from={win.from} to={win.to} formatY={fmtCount} />
          : <div className="cc-empty" style={{ height: 80 }}>窗口内暂无缓存命中（缓存随使用逐步积累）</div>}
      </div>

      {/* 用户 token 用量 — top users by totalTokens in the window. */}
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
        <CardTitle>用户 token 用量</CardTitle>
        <div style={summaryStyle}>
          Top {topUsers.length}，按窗口内总 token 消耗降序排列
        </div>
        {topUsers.length === 0
          ? <div className="cc-empty" style={{ height: 80 }}>暂无数据</div>
          : (
            <table className="cc-table">
              <thead>
                <tr>
                  <th>用户</th>
                  <th style={{ textAlign: "right" }}>token 用量</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((u) => (
                  <tr key={u.actor}>
                    <td>{u.actor}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {fmtTokens(u.totalTokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>

      {/* 按模型用量 — per-model usage (turns/tokens/cost) over the window. */}
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
        <CardTitle>按模型用量</CardTitle>
        <div style={summaryStyle}>
          各对话模型在窗口内的轮次、token 与估算成本（按总 token 降序）
        </div>
        {models.length === 0
          ? <div className="cc-empty" style={{ height: 80 }}>暂无数据</div>
          : (
            <table className="cc-table">
              <thead>
                <tr>
                  <th>模型</th>
                  <th style={{ textAlign: "right" }}>轮次</th>
                  <th style={{ textAlign: "right" }}>输入 token</th>
                  <th style={{ textAlign: "right" }}>输出 token</th>
                  <th style={{ textAlign: "right" }}>总 token</th>
                  <th style={{ textAlign: "right" }}>估算成本（¥）</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.model}>
                    <td>{m.model}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtCount(m.turns)}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtTokens(m.promptTokens)}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtTokens(m.completionTokens)}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtTokens(m.totalTokens)}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{m.costCny.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}
